import { api, readCache } from '../api'
import { countBetween } from '../motion'
import { routeGen } from '../route-state'
import {
  barLabel,
  bestMonthSentence,
  chartAriaLabel,
  chartScale,
  chartTapNote,
  fixItems,
  gridLabel,
  DAY_RANGE_LABELS,
  dayLabelFull,
  dayLines,
  dayRangeStart,
  dayTotalsColumns,
  dayTotalsCsv,
  dayTotalsNote,
  daysByMonth,
  inRange,
  type DayRange,
  hasFullLedger,
  isCurrentMonth,
  keptTile,
  last13,
  ledgerRecent,
  monthDetailLine,
  monthFull,
  monthRangeSubtitle,
  monthShort,
  moneyGoesColumns,
  moneyGoesNote,
  moneyLede,
  moneyNoPence,
  workerPayShown,
  niceStep,
  oneMoreCleanSentence,
  originNote,
  originPillKind,
  owedSubLine,
  pctPill,
  penceInPound,
  samePointNote,
  paceSentence,
  stillActivePct,
  ytdSub,
  last12Sub,
  type AnalysisMonth,
  type AnalysisPayload,
  type DayLine,
} from '../analysis-view'
import { lineWorkDone, lineWorker, openDayTotal } from '../day-total-panel'
import { initialsOf, lineDrawing, type Drawing } from '../picture-tiles'
import type { Customer } from '../types'
import { clear, el, loadError, money } from '../ui'
import { businessSettings } from '../business-settings'
import { getMe, worksAlone } from '../me'
import { teamDayTotals, teamDaysCsv } from '../day-totals-team'

// Analysis — the round's money and health, read-only, owner-only.
//
// Everything the screen prints is either fixed wording from the approved
// mockup (.analysis-mockup.html) or computed in analysis-view.ts from the
// /api/analysis payload; nothing is hard-coded from the mockup's numbers. The
// drawing lives here, the arithmetic lives there, so the arithmetic is tested
// on a fixture (scripts/analysis-fixture.json) rather than read off a page.
//
// A one-person round (worksAlone(), 0078; Liam, 19 Oct 2026: "B we want quality") shows no worker pay: the opening
// line, the chart key and its tap-a-bar line, Where the money goes, the Day totals and their download. A section whose
// figures hold worker pay from before the round went to one person still shows it (Sonnet's review; workerPayShown in
// analysis-view.ts). Every figure is the same; a team round's screen is exactly as before.

let payload: AnalysisPayload | null = null
// Same guard every screen uses: a stale answer from a previous screen (or a
// superseded request on this one) must never render over a newer one.
let requestSequence = 0

// el() makes HTML elements; charts need SVG ones. Attributes as strings —
// SVG geometry is all attributes, and setAttribute takes care of the rest.
const SVG_NS = 'http://www.w3.org/2000/svg'
function svgEl(tag: string, attrs: Record<string, string | number> = {}): SVGElement {
  const node = document.createElementNS(SVG_NS, tag)
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v))
  return node
}

function svgText(x: number, y: number, content: string, cls: string, anchor = 'start'): SVGElement {
  const t = svgEl('text', { x, y, class: cls, 'text-anchor': anchor })
  t.textContent = content
  return t
}

// The "one more clean" sentence wants the SUM of regular prices over book
// customers, which the analysis payload does not carry. The Work list's saved
// copy has it whenever that screen has been loaded; null means it cannot say
// and the sentence falls back (or is omitted) in analysis-view.
function workListSumPence(): number | null {
  const cached = readCache<{ customers: Customer[] }>('customers')
  const rows = cached?.data?.customers
  if (!Array.isArray(rows)) return null
  let sum = 0
  for (const c of rows) {
    if (c.status === 'active' && (c.cycle_days ?? 0) > 0 && (c.reg_price_pence ?? 0) > 0) sum += c.reg_price_pence ?? 0
  }
  return sum > 0 ? sum : null
}

// ---- small pieces -------------------------------------------------------------

function pillEl(text: string, kind: 'good' | 'amber' | 'bad' | null): HTMLElement {
  const cls =
    kind === 'good' ? 'analysis-pill-good' : kind === 'amber' ? 'analysis-pill-amber' : kind === 'bad' ? 'analysis-pill-bad' : 'analysis-pill-plain'
  return el('span', { class: `analysis-pill ${cls}` }, text)
}

// A percentage pill, or nothing at all when there is nothing to compare with.
function pctPillEl(now: number, prev: number, suffix = ''): HTMLElement | null {
  const pill = pctPill(now, prev)
  if (!pill) return null
  return pillEl(pill.text + (suffix ? ` ${suffix}` : ''), pill.kind)
}

// The headline figures count up from nothing the first time Money opens in a
// visit to the app (Liam, 2026-09-25); after that, and on every redraw, they
// simply show. countBetween always lands on the exact figure.
let countedIn = false

function statTile(k: string, v: string, sub: (HTMLElement | string)[], tone?: 'amber', count?: (k: number) => string): HTMLElement {
  const tile = el('div', { class: `analysis-stat${tone ? ` analysis-stat-${tone}` : ''}` })
  tile.append(el('span', { class: 'analysis-stat-k' }, k))
  const value = el('span', { class: 'analysis-stat-v' }, v)
  tile.append(value)
  if (count && !countedIn) countBetween(value, count, 800)
  const s = el('span', { class: 'analysis-stat-s' })
  s.append(...sub)
  tile.append(s)
  return tile
}

function sectionCard(heading: string, small: string | null): HTMLElement {
  const h = el('h3', { class: 'analysis-h' })
  h.append(heading)
  if (small) h.append(el('span', { class: 'analysis-sub' }, small))
  return h
}

function noteLine(text: string): HTMLElement {
  return el('p', { class: 'analysis-note' }, text)
}

// ---- the tiles ------------------------------------------------------------------

// Money brought from Squeegee (0043; PLAN section 6): earned, received, outstanding and credit, never one netted
// against another. Earned is done work this year (the tile above; a visit in an opening balance is not in it).
function moneyTiles(data: AnalysisPayload): HTMLElement {
  const m = data.money!
  const wrap = el('section', { 'aria-label': 'Money in, this year' })
  wrap.append(sectionCard('Money in', 'this year, each figure on its own; what is owed is on the Owed now tile'))
  const grid = el('div', { class: 'analysis-tiles' })
  grid.append(
    statTile('Earned', moneyNoPence(data.headline.ytd_pence), ['work done this year']),
    statTile('Received', moneyNoPence(m.received_pence), ['cash and bank in this year']),
    statTile('Credit held', moneyNoPence(m.credit_pence), ['for their next cleans']),
  )
  if (m.collecting_pence) grid.append(statTile('Still collecting', moneyNoPence(m.collecting_pence), ["by direct debit from Squeegee, don't chase"]))
  wrap.append(grid)
  return wrap
}

function tilesBlock(data: AnalysisPayload, short: string, alone: boolean): HTMLElement {
  const today = data.today
  const h = data.headline
  const grid = el('div', { class: 'analysis-tiles' })

  const ytdParts: (HTMLElement | string)[] = []
  const ytdPill = pctPillEl(h.ytd_pence, h.ytd_prev_pence)
  if (ytdPill) ytdParts.push(ytdPill)
  ytdParts.push(` ${ytdSub(h.ytd_prev_pence, today)}`)
  grid.append(statTile('This year so far', moneyNoPence(h.ytd_pence), ytdParts, undefined, (k) => moneyNoPence(h.ytd_pence * k)))

  const l12Parts: (HTMLElement | string)[] = []
  const l12Pill = pctPillEl(h.last12_pence, h.prev12_pence)
  if (l12Pill) l12Parts.push(l12Pill)
  l12Parts.push(` ${last12Sub(h.prev12_pence)}`)
  grid.append(statTile('Last 12 months', moneyNoPence(h.last12_pence), l12Parts, undefined, (k) => moneyNoPence(h.last12_pence * k)))

  const kept = keptTile(h.kept_month, data.months, today, alone)
  const keptLabel = h.kept_month ? `Kept by ${short}, ${monthShort(h.kept_month.month)}` : `Kept by ${short}`
  const keptPence = /^(\d+)p$/.exec(kept.value)
  grid.append(statTile(keptLabel, kept.value, [kept.sub], undefined, keptPence ? (k) => `${Math.round(Number(keptPence[1]) * k)}p` : undefined))

  const owedSub = el('span', {})
  // With money brought from Squeegee (0043) the figure Owed shows: the visits and the accounts together.
  const owedNow = data.money ? data.money.outstanding_pence : h.owed_pence
  owedSub.append(data.money?.owed_on_accounts_pence ? `${money(data.money.owed_on_accounts_pence)} from Squeegee · ` : `${owedSubLine(h.owed_count)} · `)
  owedSub.append(el('a', { href: '#/owed' }, 'open Owed'))
  grid.append(statTile('Owed now', moneyNoPence(owedNow), [owedSub], undefined, (k) => moneyNoPence(owedNow * k)))

  // The nudge, not a statistic: every day in here is work that happened and
  // was never totalled, so it is missing from every figure on this screen.
  const missing = data.missing_days ?? []
  if (missing.length) {
    const oldest = missing[missing.length - 1]!
    const sub = el('span', {})
    sub.append(`oldest ${dayLabelFull(oldest.date, Number(data.today.slice(0, 4)))} · `)
    sub.append(el('a', { href: `#/schedule?from=${oldest.date}` }, 'open the diary'))
    grid.append(statTile('Days missing a total', String(missing.length), [sub], 'amber', (k) => String(Math.round(missing.length * k))))
  }

  countedIn = true
  return grid
}

// ---- the income chart -------------------------------------------------------------

// noPay: none of the chart's months paid a worker in a one-person round (analysis-view.ts workerPayShown).
function incomeChart(chart: AnalysisMonth[], today: string, detail: HTMLElement, short: string, alone: boolean, noPay: boolean): HTMLElement {
  const card = el('div', { class: 'analysis-card' })

  const legend = el('div', { class: 'analysis-legend' })
  const keptSwatch = el('span', { class: 'analysis-swatch analysis-swatch-kept' })
  legend.append(el('span', {}, keptSwatch, `Kept by ${short}`))
  // A one-person round pays no workers, so its key has no Worker pay unless a month on the chart did.
  if (!noPay) legend.append(el('span', {}, el('span', { class: 'analysis-swatch analysis-swatch-worker' }), 'Worker pay'))
  legend.append(el('span', { class: 'analysis-legend-note' }, 'A plain bar means that month’s day totals are not all filled in yet.'))
  card.append(legend)

  const svg = svgEl('svg', { viewBox: '0 0 860 250', class: 'analysis-chart', role: 'img', 'aria-label': chartAriaLabel(chart) })
  const maxGross = chart.reduce((mx, m) => Math.max(mx, m.gross_pence), 0)
  const { step, top } = chartScale(maxGross)
  const BASE = 200
  const PLOT = 180 // px from baseline to the top gridline
  const px = (pence: number): number => (pence * PLOT) / top

  // Gridlines: the baseline plus three labelled steps up to the nice top.
  const gridG = svgEl('g', { class: 'analysis-grid' })
  gridG.append(svgEl('line', { x1: 40, x2: 850, y1: BASE, y2: BASE }))
  for (let i = 1; i <= 3; i++) {
    const y = BASE - (PLOT / 3) * i
    gridG.append(svgEl('line', { x1: 40, x2: 850, y1: y, y2: y }))
    gridG.append(svgText(4, y + 4, gridLabel(step * i), 'analysis-axis'))
  }
  svg.append(gridG)

  chart.forEach((m, i) => {
    const x = 48 + i * 62
    const h = m.gross_pence > 0 ? Math.max(1, Math.round(px(m.gross_pence))) : 0
    const y = BASE - h
    if (h > 0) {
      if (m.ledger && hasFullLedger(m) && m.ledger.gross_pence > 0) {
        // The split bar: Dropby's cut dark on top, the worker's pay light
        // under it, sized from the ledger's own shares.
        const keptH = Math.max(1, Math.round((h * m.ledger.kept_pence) / m.ledger.gross_pence))
        svg.append(svgEl('rect', { x, y, width: 44, height: keptH, class: 'analysis-bar-kept' }))
        svg.append(svgEl('rect', { x, y: y + keptH, width: 44, height: Math.max(0, h - keptH), class: 'analysis-bar-worker' }))
      } else {
        svg.append(svgEl('rect', { x, y, width: 44, height: h, class: 'analysis-bar-worker' }))
      }
      if (isCurrentMonth(m.month, today)) {
        svg.append(svgEl('rect', { x, y, width: 44, height: h, class: 'analysis-bar-now' }))
      }
      svg.append(svgText(x + 22, y - 6, moneyNoPence(m.gross_pence), 'analysis-barval', 'middle'))
    }
    svg.append(svgText(x + 22, 218, barLabel(m.month, today), 'analysis-mon', 'middle'))
    // The year sits under January's bar, and under the first bar whatever
    // month the window starts on.
    const isJan = parseInt(m.month.slice(5, 7), 10) === 1
    if (isJan || i === 0) svg.append(svgText(x + 22, 234, m.month.slice(0, 4), 'analysis-mon', 'middle'))

    // The tap target is wider than the bar: 62px of pitch, not 44 of ink.
    const hit = svgEl('rect', { x: x - 9, y: 20, width: 62, height: 214, class: 'analysis-hit', tabindex: 0, role: 'button' })
    const label = monthDetailLine(m, today, alone)
    hit.setAttribute('aria-label', label)
    const show = (): void => {
      detail.textContent = label
    }
    hit.addEventListener('click', show)
    hit.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        show()
      }
    })
    svg.append(hit)
  })

  const scroll = el('div', { class: 'analysis-scroll' })
  scroll.append(svg)
  card.append(scroll)

  const note = noteLine('')
  note.append(chartTapNote(short, noPay))
  const best = bestMonthSentence(chart)
  if (best) note.append(best)
  card.append(note)

  detail.className = 'analysis-detail'
  detail.setAttribute('aria-live', 'polite')
  card.append(detail)
  return card
}

// ---- year on year ------------------------------------------------------------------

function yearsTable(data: AnalysisPayload): HTMLElement {
  const card = el('div', { class: 'analysis-card analysis-scroll' })
  const table = el('table', { class: 'analysis-table' })
  const headRow = el('tr', {})
  for (const [label, num] of [
    ['Year', false],
    ['Income', true],
    ['Cleans', true],
    ['Avg per clean', true],
    ['New customers', true],
  ] as const) {
    headRow.append(el('th', { class: num ? 'analysis-num' : '' }, label))
  }
  table.append(el('thead', {}, headRow))

  const body = el('tbody', {})
  data.years.forEach((y, i) => {
    const row = el('tr', {})
    row.append(el('td', {}, y.to_date ? `${y.year} so far` : String(y.year)))
    const income = el('td', { class: 'analysis-num' })
    income.append(moneyNoPence(y.gross_pence))
    // The current year compares against last year AT THE SAME POINT, not the
    // whole year — anything else would understate the growth.
    const pill = y.to_date && y.prev_same_point_pence !== null
      ? pctPillEl(y.gross_pence, y.prev_same_point_pence, 'vs same point')
      : i > 0
        ? pctPillEl(y.gross_pence, data.years[i - 1].gross_pence)
        : null
    if (pill) income.append(' ', pill)
    row.append(income)
    row.append(el('td', { class: 'analysis-num' }, String(y.cleans)))
    row.append(el('td', { class: 'analysis-num' }, y.avg_pence === null ? '—' : money(y.avg_pence)))
    row.append(el('td', { class: 'analysis-num' }, y.new_customers > 0 ? String(y.new_customers) : '—'))
    body.append(row)
  })
  table.append(body)
  card.append(table)

  const note = noteLine(`${samePointNote(data.today)} ${paceSentence(data.headline.ytd_pence, data.today) ?? ''}`.trim())
  card.append(note)
  return card
}

// ---- where the money goes ------------------------------------------------------------

function moneyGoesTable(data: AnalysisPayload, short: string, noPay: boolean): HTMLElement {
  const card = el('div', { class: 'analysis-card analysis-scroll' })
  const recent = ledgerRecent(data.months)
  if (recent.length === 0) {
    card.append(el('p', { class: 'analysis-empty' }, 'No day totals yet. Fill in a day on the diary and this table appears.'))
    return card
  }
  const table = el('table', { class: 'analysis-table' })
  const headRow = el('tr', {})
  for (const [label, num] of moneyGoesColumns(short, noPay)) {
    headRow.append(el('th', { class: num ? 'analysis-num' : '' }, label))
  }
  table.append(el('thead', {}, headRow))

  const body = el('tbody', {})
  for (const m of recent) {
    const ledger = m.ledger!
    const row = el('tr', {})
    const label = isCurrentMonth(m.month, data.today) ? `${monthFull(m.month)} so far` : monthFull(m.month)
    row.append(el('td', {}, label))
    row.append(el('td', { class: 'analysis-num' }, moneyNoPence(ledger.gross_pence)))
    if (!noPay) row.append(el('td', { class: 'analysis-num' }, moneyNoPence(ledger.worker_pence)))
    row.append(el('td', { class: 'analysis-num' }, moneyNoPence(ledger.kept_pence)))
    row.append(el('td', { class: 'analysis-num' }, penceInPound(ledger.kept_pence, ledger.gross_pence)))
    const days = el('td', {})
    days.append(pillEl(`${ledger.days_filled} of ${m.days_worked}`, hasFullLedger(m) ? 'good' : 'amber'))
    if (!hasFullLedger(m)) days.append(' ', el('a', { href: '#/schedule' }, 'fill in'))
    row.append(days)
    body.append(row)
  }
  table.append(body)
  card.append(table)
  card.append(noteLine(moneyGoesNote(recent, noPay)))
  return card
}

// ---- day totals -------------------------------------------------------------------------
//
// The spreadsheet's "Day's Total" tab, which is the one thing the owners kept
// the Google Sheet open for after the app took over everything else. Lewis,
// 19 Sep 2026: "there's no days total tab... I wonder if there's a way of
// putting that on the app as well so that we can keep track of the taxes kind
// of side of it."
//
// It records nothing new. Every row is a day total already entered on the
// diary, read a day at a time instead of a month at a time. One row per day
// AND worker, the way the day totals are actually kept: a day two people
// worked is two totals, and one of them missing makes the day half-entered.
//
// The days with work and NO total are in the same table, in amber, because a
// list of totals that quietly leaves out the days nobody filled in is the
// exact way a tax figure comes out short.

// Which range the table is showing. Module-level, so changing it does not
// re-fetch and coming back to the screen starts from the common case.
let dayRange: DayRange = 'month'

// The lines, the columns and the download are in analysis-view.ts (dayLines, dayTotalsColumns, dayTotalsCsv), so a
// one-person round's table and its download are checked without a page.

function downloadCsv(data: AnalysisPayload, noPay: boolean): void {
  const blob = new Blob([dayTotalsCsv(dayLines(data, dayRange), noPay)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = el('a', { href: url, download: `roundly-day-totals-${dayRangeStart(dayRange, data.today)}-to-${data.today}.csv` })
  document.body.append(a)
  a.click()
  a.remove()
  // Revoked on the next turn of the loop: revoking straight away races the
  // download in Safari, which has not read the blob yet.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

// Design B for Day totals (Liam picked it, 24 Oct 2026; https://claude.ai/artifact/74rMZJhQMjncqL4HMiF7SN): each day opens
// as the whole day from one button that says so, "Fill in this day" on a day with no total and "Change this day" on one
// with it ("the add button only looks like it adds to worker paid amount but then opens and you can edit everything").
// The day total just saved shows a Saved tick beside its button once Money is drawn again (the movement set's tag, 160ms;
// Liam, 24 Oct 2026, "where else in the app", 7). Once, then it goes: the next drawing of the day totals takes it, so a
// redraw or an Undo never shows it again (Astra, 24 Oct 2026).
let savedLine: string | null = null
const lineKey = (line: DayLine): string => `${line.date}|${lineWorker(line) ?? ''}`
function savedTag(line: DayLine): HTMLElement[] {
  if (savedLine !== lineKey(line)) return []
  const tag = el('span', { class: 'analysis-day-saved rd-tag-in', role: 'status' }, '✓ Saved')
  window.setTimeout(() => tag.remove(), 2500)
  return [tag]
}
function dayButton(line: DayLine, open: (line: DayLine, opener: HTMLElement) => void, label: string): HTMLButtonElement {
  const fill = line.kind !== 'total'
  // Named with the day and who worked: a day two people worked has two buttons.
  const who = lineWorker(line)
  const b = el('button', { type: 'button', class: `analysis-day-open${fill ? ' is-fill' : ''}`, 'aria-label': `${fill ? 'Fill in' : 'Change'} ${label}${who ? `, ${who}` : ''}` },
    ...(fill ? [] : [lineDrawing('pencil', 15)]), el('span', {}, fill ? 'Fill in this day' : 'Change this day'), ...(fill ? [lineDrawing('chevronRight', 15)] : [])) as HTMLButtonElement
  b.addEventListener('click', () => open(line, b))
  return b
}

function dayTile(drawing: Drawing, label: string, value: string, tone: '' | 'is-missing' | 'is-quiet' = ''): HTMLElement {
  return el('div', { class: `analysis-day-tile${tone ? ` ${tone}` : ''}` },
    el('span', { class: 'analysis-day-tile-ring', 'aria-hidden': 'true' }, lineDrawing(drawing, 15)),
    el('span', { class: 'analysis-day-tile-label' }, label), el('strong', {}, value))
}

// On a phone the table is a card per day: who worked, then Work done, Paid worker and Kept as tiles (they only show),
// and the day's button under them; each month ends with its totals, as the table's dark line does.
function dayCards(lines: DayLine[], data: AnalysisPayload, noPay: boolean, thisYear: number, open: (line: DayLine, opener: HTMLElement) => void): HTMLElement {
  const list = el('div', { class: 'analysis-day-cards' })
  let month = ''
  let gross = 0, paid = 0, kept = 0
  const monthBar = (): void => {
    if (!month) return
    list.append(el('p', { class: 'analysis-day-cards-month' }, el('strong', {}, monthFull(month)),
      el('span', {}, `Work done ${money(gross)}${noPay ? '' : ` · Paid ${money(paid)}`} · Kept ${money(kept)}`)))
  }
  for (const line of lines) {
    const m = line.date.slice(0, 7)
    if (m !== month) { monthBar(); month = m; gross = paid = kept = 0 }
    const label = dayLabelFull(line.date, thisYear)
    const who = lineWorker(line)
    const done = lineWorkDone(line)
    const cleans = line.row.cleans
    const card = el('section', { class: `analysis-day-card${line.kind === 'missing' ? ' is-missing' : ''}`, 'aria-label': who ? `${label}, ${who}` : label })
    const head = el('div', { class: 'analysis-day-card-head' },
      el('span', { class: 'analysis-day-card-ring', 'aria-hidden': 'true' }, who ? initialsOf(who) : lineDrawing('person', 16)),
      el('span', { class: 'analysis-day-card-words' }, el('a', { href: `#/schedule?from=${line.date}` }, label),
        el('span', {}, `${noPay ? '' : `${who || 'unassigned'} · `}${cleans} ${cleans === 1 ? 'clean' : 'cleans'}`)))
    if (line.kind === 'total') head.append(el('span', { class: 'analysis-day-card-tick', role: 'img', 'aria-label': 'Day total in' }, lineDrawing('tick', 14)))
    const tiles = el('div', { class: `analysis-day-tiles${noPay ? ' is-two' : ''}` }, dayTile('pound', 'Work done', money(done)))
    if (!noPay) {
      tiles.append(line.kind === 'total' ? dayTile('person', 'Paid worker', money(line.row.worker_pence))
        : line.kind === 'missing' ? dayTile('hourglass', 'Paid worker', 'Not entered', 'is-missing')
        : dayTile('person', 'Paid worker', '—', 'is-quiet'))
    }
    tiles.append(line.kind === 'total' ? dayTile('bank', 'Kept', money(line.row.kept_pence)) : dayTile('bank', 'Kept', '—', 'is-quiet'))
    card.append(head, tiles)
    if (line.kind === 'total' && line.row.notes) card.append(el('p', { class: 'analysis-day-card-note' }, line.row.notes))
    card.append(dayButton(line, open, label), ...savedTag(line))
    list.append(card)
    gross += done
    if (line.kind === 'total') { paid += line.row.worker_pence; kept += line.row.kept_pence }
  }
  monthBar()
  return list
}

function dayTotalsTable(data: AnalysisPayload, rerender: () => void, noPay: boolean): HTMLElement {
  const card = el('div', { class: 'analysis-card' })

  const chips = el('div', { class: 'analysis-chips' })
  for (const range of ['month', 'three', 'year'] as DayRange[]) {
    const chip = el('button', { type: 'button', class: `analysis-chip${dayRange === range ? ' analysis-chip-on' : ''}`, 'aria-pressed': dayRange === range ? 'true' : 'false' }, DAY_RANGE_LABELS[range])
    chip.addEventListener('click', () => { dayRange = range; rerender() })
    chips.append(chip)
  }
  const csv = el('button', { type: 'button', class: 'analysis-csv' }, 'Download for the accountant')
  csv.addEventListener('click', () => downloadCsv(data, noPay))
  chips.append(csv)
  card.append(chips)

  const lines = dayLines(data, dayRange)
  if (lines.length === 0) {
    card.append(el('p', { class: 'analysis-empty' }, `No days in ${DAY_RANGE_LABELS[dayRange].toLowerCase()}. Enter a day total on the diary and it appears here.`))
    return card
  }

  const scroll = el('div', { class: 'analysis-scroll analysis-days-scroll' })
  const table = el('table', { class: 'analysis-table analysis-days-table' })
  const headRow = el('tr', {})
  for (const [text, num] of dayTotalsColumns(noPay)) {
    headRow.append(el('th', { class: num ? 'analysis-num' : '' }, text))
  }
  headRow.append(el('th', { class: 'analysis-days-act' }, el('span', { class: 'sr-only' }, 'Change')))
  table.append(el('thead', {}, headRow))
  const thisYear = Number(data.today.slice(0, 4))
  const open = (line: DayLine, opener: HTMLElement): void =>
    openDayTotal(line, { data, noPay, alone: worksAlone(), label: dayLabelFull(line.date, thisYear),
      // Only while Money is the screen: a save landing after leaving never draws Money over another screen.
      saved: () => { const key = lineKey(line); savedLine = key; window.setTimeout(() => { if (savedLine === key) savedLine = null }, 3000) },
      reload: () => { if (window.location.hash.startsWith('#/analysis')) void load() } }, opener)

  const body = el('tbody', {})
  let month = ''
  let gross = 0, worker = 0, kept = 0, cleans = 0
  const subtotal = (): void => {
    if (!month) return
    const row = el('tr', { class: 'analysis-days-foot' })
    row.append(el('td', {}, monthFull(month)))
    if (!noPay) row.append(el('td', {}, ''))
    row.append(el('td', { class: 'analysis-num' }, money(gross)))
    if (!noPay) row.append(el('td', { class: 'analysis-num' }, money(worker)))
    row.append(el('td', { class: 'analysis-num' }, money(kept)))
    row.append(el('td', { class: 'analysis-num' }, String(cleans)))
    row.append(el('td', {}, ''))
    row.append(el('td', {}, ''))
    body.append(row)
  }
  for (const line of lines) {
    const m = line.date.slice(0, 7)
    if (m !== month) { subtotal(); month = m; gross = worker = kept = cleans = 0 }
    const row = el('tr', { class: line.kind === 'missing' ? 'analysis-day-missing' : '' })
    // The day links to the diary at that week, so a figure that looks wrong is
    // one tap from the visits behind it.
    row.append(el('td', {}, el('a', { href: `#/schedule?from=${line.date}` }, dayLabelFull(line.date, Number(data.today.slice(0, 4))))))
    if (line.kind === 'missing') {
      // No day total entered: Work done is the visits marked done that day, so a day of finished work never reads
      // as nothing (Liam, 9 Oct 2026: "Thursday doesnt have the work done even all the work was done"); what the
      // worker was paid and what was kept come only from the day total, so those still say it is not entered.
      if (!noPay) row.append(el('td', {}, line.row.worker || 'unassigned'))
      row.append(el('td', { class: 'analysis-num analysis-from-visits', title: 'From the visits marked done; no day total entered' }, money(line.row.pence)))
      const cell = el('td', noPay ? { class: 'analysis-num' } : { class: 'analysis-num', colspan: '2' })
      cell.append(pillEl('not entered', 'amber'))
      row.append(cell)
      row.append(el('td', { class: 'analysis-num' }, String(line.row.cleans)))
      row.append(el('td', {}, 'Work done is from the visits marked done'))
      gross += line.row.pence
      cleans += line.row.cleans
    } else if (line.kind === 'finished') {
      // A one-person round's day finished with Finish my day and no day total: it counts as having its total, so it
      // is not amber. Work done is from the visits marked done, as above; Kept comes only from a day total, so it
      // stays blank rather than a figure Kept and Where the money goes do not hold.
      if (!noPay) row.append(el('td', {}, line.row.worker || 'unassigned'))
      row.append(el('td', { class: 'analysis-num analysis-from-visits', title: 'From the visits marked done' }, money(line.row.pence)))
      if (!noPay) row.append(el('td', { class: 'analysis-num' }, '—'))
      row.append(el('td', { class: 'analysis-num' }, '—'))
      row.append(el('td', { class: 'analysis-num' }, String(line.row.cleans)))
      row.append(el('td', {}, 'Day finished. Work done is from the visits marked done'))
      gross += line.row.pence
      cleans += line.row.cleans
    } else {
      const d = line.row
      if (!noPay) row.append(el('td', {}, d.worker || 'unassigned'))
      row.append(el('td', { class: 'analysis-num' }, money(d.gross_pence)))
      if (!noPay) row.append(el('td', { class: 'analysis-num' }, money(d.worker_pence)))
      row.append(el('td', { class: 'analysis-num' }, money(d.kept_pence)))
      row.append(el('td', { class: 'analysis-num' }, d.cleans > 0 ? String(d.cleans) : '—'))
      row.append(el('td', { class: 'analysis-day-note' }, d.notes ?? ''))
      gross += d.gross_pence; worker += d.worker_pence; kept += d.kept_pence; cleans += d.cleans
    }
    // The day opens from the end of its row (design B; Liam, 24 Oct 2026: a button naming the whole day, not Add).
    row.append(el('td', { class: 'analysis-days-act' }, dayButton(line, open, dayLabelFull(line.date, thisYear)), ...savedTag(line)))
    body.append(row)
  }
  subtotal()
  table.append(body)
  scroll.append(table)
  card.append(scroll)
  card.append(dayCards(lines, data, noPay, thisYear, open))
  savedLine = null // shown in both the computer's table and the phone's cards above, now taken
  const gaps = inRange(data.missing_days ?? [], dayRange, data.today).length
  const finished = inRange(data.finished_days ?? [], dayRange, data.today).length
  card.append(noteLine(dayTotalsNote(gaps, finished)))
  return card
}

// ---- round health ---------------------------------------------------------------------

function healthRow(label: string, value: (HTMLElement | string)[], opts: { muted?: boolean; red?: boolean } = {}): HTMLElement {
  const l = el('span', { class: opts.muted ? 'analysis-kv-mut' : '' }, label)
  const v = el('span', { class: `analysis-kv-num${opts.red ? ' analysis-kv-red' : ''}` })
  v.append(...value)
  const row = el('div', { class: 'analysis-kv-row' })
  row.append(l, v)
  return row
}

function healthBlock(data: AnalysisPayload): HTMLElement {
  const h = data.health
  const wrap = el('div', { class: 'analysis-two' })

  const book = el('div', { class: 'analysis-card' })
  const kv = el('div', { class: 'analysis-kv' })
  kv.append(healthRow('Active customers', [String(h.active)]))
  kv.append(healthRow('with a cycle set', [String(h.active_with_cycle)], { muted: true }))
  kv.append(healthRow('no cycle, so never in Messages', [String(h.active_no_cycle)], { muted: true, red: true }))
  kv.append(healthRow('Lapsed', [String(h.lapsed)]))
  kv.append(healthRow('Book value per year', [moneyNoPence(h.book_pence_per_year)]))
  kv.append(healthRow(`from the ${h.book_customers} with a cycle and a price`, ['']))
  book.append(kv)
  wrap.append(book)

  const chase = el('div', { class: 'analysis-card' })
  const kv2 = el('div', { class: 'analysis-kv' })
  kv2.append(healthRow('Overdue for a clean', [String(h.overdue_count)]))
  kv2.append(
    healthRow(`worth ${moneyNoPence(h.overdue_value_pence)} of cleans right now`, [el('a', { href: '#/rebook' }, 'Messages')], { muted: true }),
  )
  if (h.adherence) {
    kv2.append(healthRow('Cleans against cycle, last 12 months', [`${h.adherence.pct}%`]))
    kv2.append(healthRow(`${h.adherence.done} done of ${h.adherence.expected} the cycles promised`, [''], { muted: true }))
  } else {
    kv2.append(healthRow('Cleans against cycle, last 12 months', ['—']))
    kv2.append(healthRow('nobody has a cycle and a clean in the last year yet', [''], { muted: true }))
  }
  kv2.append(healthRow('Lapsed but worth winning back', [String(h.winback.count)]))
  kv2.append(healthRow(`${moneyNoPence(h.winback.value_pence_per_year)} a year at their old price and cycle`, [''], { muted: true }))
  chase.append(kv2)
  wrap.append(chase)

  return wrap
}

// ---- where customers come from ----------------------------------------------------------

function originsTable(data: AnalysisPayload): HTMLElement {
  const card = el('div', { class: 'analysis-card analysis-scroll' })
  if (data.origins.length === 0) {
    card.append(el('p', { class: 'analysis-empty' }, 'No customers yet, so there is nothing to break down by source.'))
    return card
  }
  const year = data.today.slice(0, 4)
  const table = el('table', { class: 'analysis-table' })
  const headRow = el('tr', {})
  for (const [label, num] of [
    ['Source', false],
    ['Customers', true],
    ['Still active', true],
    [`Income ${year}`, true],
  ] as const) {
    headRow.append(el('th', { class: num ? 'analysis-num' : '' }, label))
  }
  table.append(el('thead', {}, headRow))

  const body = el('tbody', {})
  for (const o of data.origins) {
    const row = el('tr', {})
    row.append(el('td', {}, o.origin))
    row.append(el('td', { class: 'analysis-num' }, String(o.customers)))
    const active = el('td', { class: 'analysis-num' })
    active.append(String(o.active))
    const pct = stillActivePct(o)
    // "Not recorded" carries no verdict — there is nothing to be good or bad at.
    if (o.origin !== 'Not recorded') active.append(' ', pillEl(`${pct}%`, originPillKind(pct)))
    row.append(active)
    row.append(el('td', { class: 'analysis-num' }, moneyNoPence(o.income_year_pence)))
    body.append(row)
  }
  table.append(body)
  card.append(table)
  const note = originNote(data.origins)
  if (note) card.append(noteLine(note))
  return card
}

// ---- new customers -----------------------------------------------------------------------

function newCustomersChart(rows: { month: string; count: number }[]): HTMLElement {
  const card = el('div', { class: 'analysis-card' })
  if (rows.length === 0) {
    card.append(el('p', { class: 'analysis-empty' }, 'No first cleans in the last year.'))
    return card
  }
  const svg = svgEl('svg', { viewBox: '0 0 860 120', class: 'analysis-chart analysis-newchart', role: 'img', 'aria-label': 'New customers per month' })
  const max = rows.reduce((mx, r) => Math.max(mx, r.count), 0)
  const top = niceStep(max / 3) * 3 // a 15-count axis for a 13-count peak
  const BASE = 95
  const PLOT = 90
  rows.forEach((r, i) => {
    const x = 48 + i * 62
    const h = r.count > 0 ? Math.max(1, Math.round((r.count * PLOT) / top)) : 0
    const y = BASE - h
    if (h > 0) {
      svg.append(svgEl('rect', { x, y, width: 44, height: h, class: 'analysis-bar-kept' }))
      svg.append(svgText(x + 22, y - 6, String(r.count), 'analysis-barval', 'middle'))
    }
    svg.append(svgText(x + 22, 112, monthShort(r.month), 'analysis-mon', 'middle'))
  })
  const scroll = el('div', { class: 'analysis-scroll' })
  scroll.append(svg)
  card.append(scroll)
  return card
}

// ---- things to fix -------------------------------------------------------------------------

function fixesBlock(data: AnalysisPayload): HTMLElement {
  const totalCustomers = data.origins.reduce((sum, o) => sum + o.customers, 0)
  const items = fixItems(data.fixes, data.today, totalCustomers, data.missing_days ?? [])
  const wrap = el('div', { class: 'analysis-fix' })
  if (items.length === 0) {
    wrap.append(el('p', { class: 'analysis-empty' }, 'Nothing to fix: every customer has a cycle, a source and a price.'))
    return wrap
  }
  for (const item of items) {
    const row = el('div', { class: 'analysis-fixrow' })
    const text = el('div', { class: 'analysis-fixmain' })
    text.append(el('b', {}, item.title))
    text.append(el('div', { class: 'analysis-fixwhy' }, item.why))
    row.append(text)
    if (item.action) {
      row.append(el('a', { href: item.action.href, class: item.action.dark ? 'analysis-btn analysis-btn-dark' : 'analysis-btn' }, item.action.label))
    }
    wrap.append(row)
  }
  return wrap
}

// ---- the screen ------------------------------------------------------------------------------

function render(data: AnalysisPayload, root: HTMLElement): void {
  clear(root)
  const today = data.today
  const short = businessSettings().short_name
  // A one-person round: no worker pay on the screen (0078), except where the figures in view hold some from before.
  const alone = worksAlone()
  const pay = workerPayShown(data, dayRange, alone)

  // "Analysis" named the machinery, not the question. Lewis kept a Google
  // Sheet open for the tax figures this screen already held, because nothing
  // in the word Analysis says money (19 Sep 2026: "there's no days total
  // tab"). The tab is Money now, and the day totals come first — the round's
  // health is a different question and sits under its own heading below.
  const heading = el('div', { class: 'page-heading' })
  heading.append(el('h2', {}, 'Money'))
  // Two sources, said plainly (bug hunt 8 Oct 2026; docs/ANALYSIS-CONTRACT.md): takings are the visits marked done,
  // what the workers were paid and what was kept are the day totals on the diary.
  heading.append(el('p', { class: 'muted analysis-lede' }, moneyLede(short, !pay.any)))
  root.append(heading)
  const priceEntry = el('div', { class: 'analysis-price-review' }, el('a', { href: '#/raise-prices', class: 'btn' }, 'Review your prices'))
  root.append(priceEntry)
  void api<{reminder:boolean}>('/api/price-reviews').then(review => {
    if (review.reminder && priceEntry.isConnected) priceEntry.prepend(el('p', { class: 'muted' }, 'Time to review your prices? You have not reviewed them in the last 12 months.'))
  }).catch(() => {})
  root.append(tilesBlock(data, short, alone))
  if (data.money) root.append(moneyTiles(data))

  // A round with people to pay: Day totals a month at a time, with a tick for paying them (Close out the day, 0095;
  // Liam, 29 Oct 2026, design 2 v2). A one-person round keeps the table it had.
  if (!alone) {
    root.append(sectionCard('Day totals', 'one line a day, a month at a time'))
    const thisYear = Number(today.slice(0, 4))
    const refresh = (): void => { if (window.location.hash.startsWith('#/analysis') || window.location.hash.startsWith('#/money')) void load() }
    const open = (line: DayLine, opener: HTMLElement): void =>
      openDayTotal(line, { data, noPay: false, alone: false, label: dayLabelFull(line.date, thisYear), saved: () => {}, reload: refresh }, opener)
    const lines = [...data.days.map((row) => ({ kind: 'total' as const, date: row.date, row })), ...(data.missing_days ?? []).map((row) => ({ kind: 'missing' as const, date: row.date, row }))]
    root.append(teamDayTotals(data, data.owners ?? [getMe()?.workerName], () => render(data, root), refresh,
      () => {
        // The same figures as the table, one line a day (Astra's review).
        const blob = new Blob([teamDaysCsv(data, data.owners ?? [getMe()?.workerName])], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = el('a', { href: url, download: `roundly-day-totals-to-${data.today}.csv` })
        document.body.append(a); a.click(); a.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
      },
      (date) => lines.filter((l) => l.date === date).map((l) => dayButton(l, open, dayLabelFull(date, thisYear)))))
  } else {
    root.append(sectionCard('Day totals', 'one line per working day, the way the sheet kept it'))
    root.append(dayTotalsTable(data, () => render(data, root), !pay.days))
  }

  const chart = last13(data.months)
  root.append(sectionCard('Month by month', monthRangeSubtitle(chart)))
  if (chart.length === 0) {
    root.append(el('p', { class: 'analysis-empty' }, 'No cleans recorded yet.'))
  } else {
    const detail = el('p', {})
    root.append(incomeChart(chart, today, detail, short, alone, !pay.chart))
  }

  root.append(sectionCard('Year on year', null))
  if (data.years.length === 0) {
    root.append(el('p', { class: 'analysis-empty' }, 'No cleans recorded yet.'))
  } else {
    root.append(yearsTable(data))
  }

  root.append(sectionCard('Where the money goes', 'from the day totals'))
  root.append(moneyGoesTable(data, short, !pay.moneyGoes))

  // Everything above answers a money question and can be handed to an
  // accountant. Everything below is the round's condition, which is a
  // different question and must not be mistaken for a tax figure.
  root.append(el('div', { class: 'analysis-divider' },
    el('h2', {}, 'How the round is doing'),
    el('p', { class: 'muted' }, 'Not tax figures.')))

  root.append(sectionCard('Round health', null))
  root.append(healthBlock(data))
  const avg = data.years.find((y) => y.to_date)?.avg_pence ?? null
  const oneMore = oneMoreCleanSentence(data.health, avg, workListSumPence())
  if (oneMore) root.append(noteLine(oneMore))

  root.append(sectionCard('Where customers come from', null))
  root.append(originsTable(data))

  root.append(sectionCard('New customers', 'first clean, by month'))
  root.append(newCustomersChart(data.new_customers))

  root.append(sectionCard('Things to fix', 'each one makes the numbers above truer'))
  root.append(fixesBlock(data))
}

async function load(): Promise<void> {
  const gen = routeGen
  const sequence = ++requestSequence
  const root = document.querySelector<HTMLElement>('#screen')!
  try {
    const result = await api<AnalysisPayload>('/api/analysis', 'analysis')
    if (gen !== routeGen || sequence !== requestSequence) return
    payload = result
    render(payload, root)
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence) return
    const cached = readCache<AnalysisPayload>('analysis')
    if (cached) {
      payload = cached.data
      render(cached.data, root)
      loadError(root, (err as Error).message, () => void load(), cached.at)
    } else {
      clear(root)
      loadError(root, (err as Error).message, () => void load())
    }
  }
}

export function mountAnalysis(): void {
  void load()
}

export function analysisOnDataChanged(): void {
  void load()
}
